import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { List as VirtualList } from "react-window";
import { Search as SearchIcon, FileText, Users as UsersIcon, ChevronRight, X } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { searchApi } from "@/lib/api";

const typeLabel = {
  DEPOT: "Dépôt",
  RESTITUTION: "Restitution",
  DELIVRANCE: "Délivrance",
};

const fmt = (d) => {
  try {
    return new Date(d).toLocaleDateString("fr-FR");
  } catch {
    return "";
  }
};

// Live search with 250ms debounce + monotonic request id to avoid stale
// responses overwriting newer ones (the recurrence bug from a previous build).
const DEBOUNCE_MS = 250;

// ─────────────── Row components (shared between plain & virtualised rendering)
function SearchClientRow({ client, onClick }) {
  return (
    <button
      data-testid={`search-client-${client.id}`}
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--vidoc-bg-secondary)] text-left h-16"
    >
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">
          {client.nom} {client.prenom}
        </div>
        <div className="text-xs text-[var(--vidoc-text-secondary)] truncate">
          {[client.numero_client && `N° ${client.numero_client}`, client.email, client.ville]
            .filter(Boolean)
            .join(" · ")}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-[var(--vidoc-text-tertiary)]" />
    </button>
  );
}

function SearchOperationRow({ op, onClick }) {
  return (
    <button
      data-testid={`search-op-${op.id}`}
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--vidoc-bg-secondary)] text-left h-16"
    >
      <FileText className="w-5 h-5 text-[var(--vidoc-primary)] shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">
          {typeLabel[op.type] || op.type} —{" "}
          {op.client_name_snapshot || "Client inconnu"}
        </div>
        <div className="text-xs text-[var(--vidoc-text-secondary)]">
          N° {op.id} · {fmt(op.date)} · {op.utilisateur || "—"}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-[var(--vidoc-text-tertiary)]" />
    </button>
  );
}

function VirtualClientRow({ index, style, items, onClick }) {
  const c = items[index];
  if (!c) return null;
  return (
    <div style={style} className="border-b border-[var(--vidoc-border)] last:border-b-0">
      <SearchClientRow client={c} onClick={() => onClick(c.id)} />
    </div>
  );
}

function VirtualOperationRow({ index, style, items, onClick }) {
  const op = items[index];
  if (!op) return null;
  return (
    <div style={style} className="border-b border-[var(--vidoc-border)] last:border-b-0">
      <SearchOperationRow op={op} onClick={() => onClick(op.id)} />
    </div>
  );
}

export default function Recherche() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("all");
  const [results, setResults] = useState({ clients: [], operations: [] });
  const [loading, setLoading] = useState(false);

  // Monotonic counter — only the most recent request's response is applied.
  const requestIdRef = useRef(0);
  // Debounce timer
  const timerRef = useRef(null);

  // useCallback so we can include `runSearch` in the debounce effect's deps
  // without re-creating the timer on every render. All inner refs/setters
  // (requestIdRef, setLoading, setResults, searchApi) are stable, so the
  // empty deps array is safe.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const runSearch = useCallback((q, searchType) => {
    // If the box is empty, clear results immediately (no roundtrip).
    if (!q || !q.trim()) {
      requestIdRef.current += 1; // invalidate any in-flight request
      setLoading(false);
      setResults({ clients: [], operations: [] });
      return;
    }
    const myId = ++requestIdRef.current;
    setLoading(true);
    searchApi
      .search({ query: q.trim(), search_type: searchType })
      .then((data) => {
        if (myId !== requestIdRef.current) return; // stale, ignore
        setResults(data || { clients: [], operations: [] });
      })
      .catch(() => {
        if (myId !== requestIdRef.current) return;
        setResults({ clients: [], operations: [] });
      })
      .finally(() => {
        if (myId !== requestIdRef.current) return;
        setLoading(false);
      });
  }, []);

  // Debounced live search whenever query or tab changes
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => runSearch(query, tab), DEBOUNCE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [query, tab, runSearch]);

  // Submitting the form (Enter key) just triggers an immediate search (no debounce)
  const handleSubmit = (e) => {
    e.preventDefault();
    if (timerRef.current) clearTimeout(timerRef.current);
    runSearch(query, tab);
  };

  const switchTab = (t) => {
    setTab(t);
  };

  const handleClear = () => {
    setQuery("");
  };

  const hasResults =
    (results.clients?.length || 0) + (results.operations?.length || 0) > 0;

  // Stable rowProps objects for the virtualised lists. Recreating them on
  // every render would force react-window to invalidate its row cache.
  const clientsRowProps = useMemo(
    () => ({
      items: results.clients,
      onClick: (id) => navigate(`/clients/${id}`),
    }),
    [results.clients, navigate],
  );
  const operationsRowProps = useMemo(
    () => ({
      items: results.operations,
      onClick: (id) => navigate(`/operations/${id}`),
    }),
    [results.operations, navigate],
  );

  return (
    <div data-testid="recherche-page">
      <PageHeader
        title="Recherche"
        subtitle="Clients et opérations"
      />

      <div className="px-4 md:px-8 py-6 max-w-3xl space-y-4">
        <form onSubmit={handleSubmit} className="relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--vidoc-text-tertiary)] pointer-events-none" />
          <input
            data-testid="search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Nom, n° client, email, type d'opération..."
            autoFocus
            className="w-full pl-10 pr-10 py-3 rounded-xl bg-[var(--vidoc-bg-secondary)] border border-transparent focus:border-[var(--vidoc-primary)] focus:bg-white outline-none touch-target"
          />
          {query && (
            <button
              type="button"
              data-testid="search-clear-btn"
              onClick={handleClear}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-white touch-target text-[var(--vidoc-text-secondary)]"
              aria-label="Effacer"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </form>

        <div className="flex gap-1 bg-[var(--vidoc-bg-secondary)] p-1 rounded-xl w-fit">
          {[
            { id: "all", label: "Tous" },
            { id: "clients", label: "Clients" },
            { id: "operations", label: "Opérations" },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`search-tab-${t.id}`}
              onClick={() => switchTab(t.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                tab === t.id ? "bg-white shadow-sm" : "text-[var(--vidoc-text-secondary)]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading && (
          <div className="text-center py-6 text-[var(--vidoc-text-secondary)]">
            Recherche en cours...
          </div>
        )}

        {/* Clients results */}
        {(tab === "all" || tab === "clients") && results.clients?.length > 0 && (
          <section data-testid="search-clients">
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
              <UsersIcon className="w-4 h-4" />
              Clients ({results.clients.length})
            </h3>
            <div className="bg-white rounded-xl border border-[var(--vidoc-border)] divide-y divide-[var(--vidoc-border)] overflow-hidden">
              {results.clients.length <= 100 ? (
                results.clients.map((c) => (
                  <SearchClientRow
                    key={c.id}
                    client={c}
                    onClick={() => navigate(`/clients/${c.id}`)}
                  />
                ))
              ) : (
                <VirtualList
                  rowCount={results.clients.length}
                  rowHeight={64}
                  defaultHeight={Math.min(results.clients.length * 64, 480)}
                  style={{ height: Math.min(results.clients.length * 64, 480) }}
                  overscanCount={6}
                  rowComponent={VirtualClientRow}
                  rowProps={clientsRowProps}
                />
              )}
            </div>
          </section>
        )}

        {/* Operations results */}
        {(tab === "all" || tab === "operations") && results.operations?.length > 0 && (
          <section data-testid="search-operations">
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Opérations ({results.operations.length})
            </h3>
            <div className="bg-white rounded-xl border border-[var(--vidoc-border)] divide-y divide-[var(--vidoc-border)] overflow-hidden">
              {results.operations.length <= 100 ? (
                results.operations.map((op) => (
                  <SearchOperationRow
                    key={op.id}
                    op={op}
                    onClick={() => navigate(`/operations/${op.id}`)}
                  />
                ))
              ) : (
                <VirtualList
                  rowCount={results.operations.length}
                  rowHeight={64}
                  defaultHeight={Math.min(results.operations.length * 64, 480)}
                  style={{ height: Math.min(results.operations.length * 64, 480) }}
                  overscanCount={6}
                  rowComponent={VirtualOperationRow}
                  rowProps={operationsRowProps}
                />
              )}
            </div>
          </section>
        )}

        {!loading && !hasResults && (
          <div className="text-center py-12 text-[var(--vidoc-text-secondary)]">
            {query ? "Aucun résultat" : "Saisissez un terme pour rechercher"}
          </div>
        )}
      </div>
    </div>
  );
}
